/**
 * ExperiencePage — /upplevelse/:slug
 *
 * Strictly editorial chapter page. No proximity modules.
 *
 * Canonical section order:
 *   1. Hero + Header
 *   2. Curated Places (max 6, hideIfEmpty)
 *   3. Curated Activities (max 3, hideIfEmpty)
 *   4. Bottom link → Explore
 *
 * SoT: experienceConfig.ts drives titles & labels.
 */
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar } from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingMinimalFooter from '@/components/landing/LandingMinimalFooter';
import ExperienceHeader from '@/components/experience/ExperienceHeader';
import { useExperienceData } from '@/hooks/useExperienceData';
import { useExperienceBySlug } from '@/hooks/useExperiences';
import { getExperienceConfig } from '@/lib/experiences/experienceConfig';
import { Skeleton } from '@/components/ui/skeleton';
import { ContentCard } from '@/components/ui/content-card';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { resolveVenueText } from '@/lib/venue/resolveVenueText';
import { humanizeLandingDate } from '@/lib/events/humanizeLandingDate';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import type { ExperienceEvent } from '@/hooks/useExperienceData';

export default function ExperiencePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: experience, isLoading: expLoading } = useExperienceBySlug(slug);
  const { data, isLoading } = useExperienceData(slug);

  // Resolve config from shared SoT, then DB fallback
  const config = slug ? getExperienceConfig(slug) : undefined;
  const title = config?.title ?? experience?.title ?? data?.experience?.title ?? '';
  const subtitle = config?.subtitle ?? '';
  const intro = config?.intro ?? experience?.intro ?? data?.experience?.intro;
  

  // Hero image: first curated place cover as visual anchor
  const firstPlaceId = data?.featuredPlaces?.[0]?.id;
  const firstPlaceCoverUrl = useCoverImageUrl('place', firstPlaceId, 1280);
  const heroImageUrl = firstPlaceCoverUrl || null;

  // Dynamic activity section title
  const activitiesSectionTitle = config?.activitiesSectionTitle
    ? config.activitiesSectionTitle.replace(/^Aktiviteter/, 'Det händer')
    : 'Det händer här';

  // Not found
  if (!expLoading && !isLoading && !experience && !data?.experience && !config) {
    return (
      <div className="flex min-h-screen flex-col">
        <LandingHeader variant="lens" />
        <main className="flex-1 container max-w-2xl px-5 pt-24 pb-24">
          <h1 className="font-display text-xl font-semibold text-foreground mb-2">Upplevelsen finns inte</h1>
          <p className="text-muted-foreground">Vi kunde inte hitta den här upplevelsen.</p>
        </main>
        <LandingMinimalFooter />
      </div>
    );
  }

  // Section data — strictly curated, no proximity
  const curatedPlaces = (data?.featuredPlaces ?? []).slice(0, 6);
  const eventList = (data?.events ?? []).slice(0, 3);

  const hasPlaces = curatedPlaces.length > 0;
  const hasEvents = eventList.length > 0;
  const hasContent = hasPlaces || hasEvents;

  return (
    <div className="flex min-h-screen flex-col">
      <LandingHeader variant="lens" />

      <main className="flex-1">
        {/* Editorial header with optional hero */}
        <ExperienceHeader
          title={title}
          subtitle={subtitle}
          intro={intro}
          heroImageUrl={heroImageUrl}
        />

        {/* Section 1: Curated Places — flows directly from intro, no section title */}
        {(isLoading || hasPlaces) && (
          <section className="container max-w-2xl px-5 pt-2 sm:pt-4 pb-4">
            {isLoading ? (
              <CardSkeletons count={3} />
            ) : (
              <div className="flex flex-col gap-6">
                {curatedPlaces.map((place) => (
                  <PlaceContentCard key={place.id} place={place} variant="experience" />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Section 2: Curated Activities as compact cards (hideIfEmpty) */}
        {(isLoading || hasEvents) && (
          <ChapterSection title={activitiesSectionTitle}>
            {isLoading ? (
              <CardSkeletons count={2} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {eventList.map((event) => (
                  <ActivityCompactCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </ChapterSection>
        )}

        {/* Empty state — only after loading */}
        {!isLoading && !hasContent && (
          <section className="container max-w-2xl px-5 py-12 text-center">
            <p className="text-muted-foreground text-sm">
              Det finns inga kuraterade platser eller aktiviteter just nu.
            </p>
          </section>
        )}

        {/* Bottom link to Explore — broader discovery */}
        {!isLoading && (
          <section className="container max-w-2xl px-5 pt-8 pb-4">
            <Link
              to="/explore"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
            >
              Utforska allt i Dalarna
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </section>
        )}

        {/* Breathing room */}
        <div className="h-12 sm:h-20" />
      </main>

      <LandingMinimalFooter />
    </div>
  );
}

/* ── Chapter section wrapper ── */
function ChapterSection({
  title,
  children,
  spacing,
}: {
  title: string;
  children: React.ReactNode;
  spacing?: 'first' | 'last';
}) {
  return (
    <section
      className={`container max-w-2xl px-5 ${
        spacing === 'first' ? 'pt-2 sm:pt-4' : 'pt-8 sm:pt-10'
      } ${spacing === 'last' ? 'pb-8' : 'pb-4'}`}
    >
      <h2 className="font-display text-lg sm:text-xl font-semibold text-foreground tracking-[-0.01em] mb-5">
        {title}
      </h2>
      {children}
    </section>
  );
}

/* ── Compact activity card (reuses landing card pattern) ── */
function ActivityCompactCard({ event }: { event: ExperienceEvent }) {
  const coverUrl = useCoverImageUrl('event', event.id, 640);

  const { venue_text } = resolveVenueText({
    use_producer_address: event.useProducerAddress,
    custom_address: event.customAddress,
    producer_name: event.producerName,
    producer_city: event.producerCity,
  });

  const dateLabel = event.startDate
    ? format(new Date(event.startDate), 'd MMM', { locale: sv })
    : null;
  const relativeLabel = humanizeLandingDate(event.startDate);
  const dateLine = relativeLabel && dateLabel
    ? `${relativeLabel} · ${dateLabel}`
    : relativeLabel || dateLabel;

  return (
    <Link
      to={`/event/${event.id}`}
      className="group flex flex-col rounded-xl border border-border/40 bg-card overflow-hidden transition-all duration-200 hover:border-border hover:shadow-sm active:scale-[0.99]"
    >
      <div className="aspect-[3/2] overflow-hidden bg-muted relative">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <Calendar className="h-10 w-10 text-muted-foreground/20" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 p-4">
        <h3 className="font-display text-base text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {event.title}
        </h3>

        {venue_text && (
          <div className="flex flex-col gap-0.5">
            <div className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{venue_text}</span>
            </div>
            {event.areaName && (
              <span className="text-xs text-muted-foreground/60 pl-4">
                {event.areaName}
              </span>
            )}
          </div>
        )}

        {dateLine && (
          <span className="text-xs text-muted-foreground/60 mt-0.5">{dateLine}</span>
        )}
      </div>
    </Link>
  );
}

/* ── Place wrapped in ContentCard with cover image ── */
function PlaceContentCard({
  place,
  variant = 'place',
}: {
  place: {
    id: string;
    slug: string;
    name: string;
    municipality: string | null;
    landskap: string | null;
    descriptionShort: string | null;
  };
  variant?: 'place' | 'experience';
}) {
  const coverUrl = useCoverImageUrl('place', place.id, 640);
  const locationText = [place.municipality, place.landskap].filter(Boolean).join(', ');

  return (
    <ContentCard
      variant={variant}
      title={place.name}
      subtitle={locationText || undefined}
      description={place.descriptionShort}
      imageUrl={coverUrl}
      href={`/place/${place.slug}`}
    />
  );
}

/* ── Skeletons ── */
function CardSkeletons({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl bg-muted/30 p-4 space-y-2">
          <Skeleton className="aspect-[3/2] w-full rounded-lg" />
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  );
}
